// pages/ProgramPage.jsx
import React, { useMemo } from 'react';
import {
  FaUniversity, FaGraduationCap, FaClipboardList, FaHashtag,
  FaFileAlt, FaGlobe, FaCogs, FaLanguage, FaSortNumericDown, FaToggleOn
} from 'react-icons/fa';
import GenericCrudPage from '../components/GenericCrudPage';
import { useColleges, useDegrees } from '../hooks/useDropdownData';

const ProgramPage = () => {
  const { colleges, loading: loadingColleges } = useColleges();
  const { degrees, loading: loadingDegrees } = useDegrees();

  const crudConfig = {
    baseUrl: process.env.REACT_APP_BASE_URL || '',
    endpoints: {
      fetch: '/master/getProgramme',
      save: '/master/saveProgramme',
      update: '/master/updateProgramme',
      delete: '/master/deletProgramme'
    },
    entityName: 'Program',
    initialFormState: {
      exName: '',
      exDuration: '',
      exDescription: '',
      exIdentifier: '',
      exLocalLangName: '',
      programValue: '',
      degree: '',
      colleges: [], // Array for multi-select
      programType: '',
      // Removed exIsActive - will always be true by default
    },
    validateForm: (form) => {
      const errors = {};
      if (!form.exName) errors.exName = 'Program Name is required.';
      if (!form.exDuration) errors.exDuration = 'Duration is required.';
      if (!form.degree) errors.degree = 'Degree is required.';
      if (form.colleges.length === 0) errors.colleges = 'At least one College is required.';
      return errors;
    }
  };

  const formFields = useMemo(() => [
    { name: 'exName', label: 'Program Name', icon: FaClipboardList, required: true },
    { name: 'exLocalLangName', label: 'Local Language Name', icon: FaLanguage },
    { name: 'exDuration', label: 'Duration (Years)', icon: FaHashtag, required: true, type: 'number' },
    { name: 'exIdentifier', label: 'Identifier', icon: FaHashtag },
    { name: 'programValue', label: 'Program Value', icon: FaSortNumericDown },
    {
      name: 'programType', label: 'Program Type', icon: FaCogs, as: 'select',
      options: [
        { value: '', label: 'Select Program Type' },
        { value: 0, label: 'Type 0' },
        { value: 1, label: 'Type 1' }
      ]
    },
    {
      name: 'degree', label: 'Degree', icon: FaGraduationCap, as: 'select', required: true,
      options: [
        { value: '', label: loadingDegrees ? 'Loading...' : 'Select Degree' },
        ...degrees.map(d => ({ value: d.id, label: d.exName }))
      ]
    },
    {
      name: 'colleges', 
      label: 'Colleges', 
      icon: FaUniversity, 
      as: 'multiselect', // Changed to multiselect
      required: true,
      options: colleges.map(c => ({ value: c.id, label: c.exName })),
      loading: loadingColleges
    },
    { name: 'exDescription', label: 'Description', icon: FaFileAlt, as: 'textarea', rows: 3, fullWidth: true },
    // Removed the switch field - will always be active by default
  ], [degrees, colleges, loadingDegrees, loadingColleges]);

  const tableColumns = [
    { key: 'exName', label: 'Name', sortable: true },
    { key: 'exDuration', label: 'Duration', sortable: true },
    { key: 'exIdentifier', label: 'Identifier', sortable: true },
    { key: 'programValue', label: 'Program Value', sortable: true },
    {
      key: 'degree',
      label: 'Degree',
      sortable: true,
      render: (item) => {
        // Check multiple possible paths for degree data
        if (item.degree?.exName) return item.degree.exName;
        if (item.degreeName) return item.degreeName;
        if (item.degreeId) {
          const degree = degrees.find(d => d.id === item.degreeId);
          return degree ? degree.exName : 'N/A';
        }
        return 'N/A';
      }
    },
    {
      key: 'colleges',
      label: 'Colleges',
      render: (item) => {
        // Check multiple possible paths for college data
        if (item.collegeArray?.length > 0) {
          return item.collegeArray.map(c => c.exName).join(', ');
        }
        if (item.colleges?.length > 0) {
          return item.colleges.map(c => c.exName || c.name).join(', ');
        }
        if (item.collegeNames) {
          return Array.isArray(item.collegeNames) ? item.collegeNames.join(', ') : item.collegeNames;
        }
        return 'N/A';
      }
    }
  ];

  // Define CSV headers with proper rendering for complex fields
  const csvHeaders = [
    { key: 'exName', label: 'Name' },
    { key: 'exDuration', label: 'Duration' },
    { key: 'exIdentifier', label: 'Identifier' },
    { key: 'programValue', label: 'Program Value' },
    { key: 'degree', label: 'Degree', render: (item) => {
      if (item.degree?.exName) return item.degree.exName;
      if (item.degreeName) return item.degreeName;
      if (item.degreeId) {
        const degree = degrees.find(d => d.id === item.degreeId);
        return degree ? degree.exName : 'N/A';
      }
      return 'N/A';
    }},
    { key: 'colleges', label: 'Colleges', render: (item) => {
      if (item.collegeArray?.length > 0) {
        return item.collegeArray.map(c => c.exName).join(', ');
      }
      if (item.colleges?.length > 0) {
        return item.colleges.map(c => c.exName || c.name).join(', ');
      }
      if (item.collegeNames) {
        return Array.isArray(item.collegeNames) ? item.collegeNames.join(', ') : item.collegeNames;
      }
      return 'N/A';
    }},
    { key: 'exIsActive', label: 'Status', render: (item) => item.exIsActive ? 'Active' : 'Inactive' }
  ];

  const transformPayload = (form, editId, userId) => {
    const payload = {
      ...form,
      id: editId,
      user: userId,
      degree: parseInt(form.degree, 10),
       colleges: Array.isArray(form.colleges)
      ? form.colleges.map(id => ({ id: parseInt(id, 10) }))
      : [], 
      programType: form.programType !== '' ? parseInt(form.programType, 10) : 0,
    exIsActive: true,
  };
    
    console.log('Payload being sent:', payload);
    return payload;
  };

  const transformToForm = (item) => ({
    exName: item.exName || '',
    exDuration: item.exDuration || '',
    exDescription: item.exDescription || '',
    exIdentifier: item.exIdentifier || '',
    exLocalLangName: item.exLocalLangName || '',
    programValue: item.programValue || '',
    degree: item.degree?.id?.toString() || item.degreeId?.toString() || '',
    // Handle multiple colleges for editing
    colleges: item.collegeArray?.map(c => c.id.toString()) || 
              item.colleges?.map(c => c.id.toString()) || 
              [],
    programType: item.programType?.toString() ?? '',
    // Removed exIsActive from form state
  });

  const getViewFields = (item) => item ? [
    { label: 'Program Name', value: item.exName, icon: FaClipboardList },
    { label: 'Duration', value: `${item.exDuration} years`, icon: FaHashtag },
    { label: 'Identifier', value: item.exIdentifier, icon: FaHashtag },
    { label: 'Program Value', value: item.programValue || 'N/A', icon: FaSortNumericDown },
    { 
      label: 'Degree', 
      value: item.degree?.exName || 
             item.degreeName || 
             degrees.find(d => d.id === item.degreeId)?.exName || 
             'N/A', 
      icon: FaGraduationCap 
    },
    { 
      label: 'Program Type', 
      value: item.programType !== undefined ? `Type ${item.programType}` : 'Not specified',
      icon: FaCogs 
    },
    { 
      label: 'Status', 
      value: item.exIsActive ? 'Active' : 'Inactive',
      icon: FaToggleOn
    },
    { 
      label: 'Colleges', 
      value: item.collegeArray?.map(c => c.exName).join(', ') || 
             item.colleges?.map(c => c.exName || c.name).join(', ') || 
             'N/A', 
      icon: FaUniversity, 
      fullWidth: true 
    },
    { label: 'Description', value: item.exDescription, icon: FaFileAlt, fullWidth: true },
  ] : [];

  return (
    <GenericCrudPage
      title="Program"
      config={crudConfig}
      formFields={formFields}
      tableColumns={tableColumns}
      csvHeaders={csvHeaders}
      csvFilename="programs.csv"
      transformPayload={transformPayload}
      transformToForm={transformToForm}
      getViewFields={getViewFields}
      emptyIcon={FaClipboardList}
      searchPlaceholder="Search programs, degrees, colleges..."
    />
  );
};

export default ProgramPage;